import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, ArrowRight, CalendarCog, CalendarHeart, CheckCircle2, ChevronDown, ChevronRight, Gift, Layers, Link2, ListTree, MapPin, RefreshCw,
  ShieldCheck, SlidersHorizontal, XCircle,
} from 'lucide-react'
import * as api from '../api'
import { Chip, dmy, ErrorBox, Kpi, Loading, n0, Panel, pctx, PyHeader } from '../py'
import { pyTo } from '../links'

// Stephanet's Setup Overview (custom/pay15/SetupOverview.jsx), ported unchanged. Its linked ERP screens are copies kept "in step";
// here they read the same records, so every one is "Same records" and there is nothing to bring in step.
const L = ({ k, className, children }) => { const to = pyTo(k); return to ? <Link className={className} to={to}>{children}</Link> : <span className={`${className || ''} static`}>{children}</span> }

// every Setup area, what it holds and the screens that read it (the workbook's flow: GUIDE › THE FLOW)
const AREAS = [
  { key: 'py-date-control', title: 'Date Control', Ico: CalendarCog, holds: 'Payroll month, cut-off day, work week',
    feeds: [['py-site-sheets', 'Site Sheets'], ['py-att-import', 'Attendance Import'], ['attendance-mark', 'Mark Daily Attendance'], ['py-engine', 'Payroll Engine'],
      ['py-benefits-due', 'Benefits Due'], ['payroll-engine', 'Payroll Runs']] },
  { key: 'py-setup', title: 'Setup Rules', Ico: SlidersHorizontal, holds: 'Rates, PAYE bands, salary split, absence profiles, OT tiers, who pays',
    feeds: [['py-engine', 'Payroll Engine'], ['py-basic-pay', 'Basic Pay'], ['py-daily-pay', 'Daily Pay'], ['py-paye', 'PAYE Schedule'], ['py-pension', 'Pension Schedule'],
      ['py-payslips', 'Payslips'], ['py-guide', 'Pay Calculator'], ['tax-engine', 'Tax Engine'], ['system-parameters', 'System Parameters']] },
  { key: 'py-projects', title: 'Projects', Ico: MapPin, holds: 'Sites, site / hazard / outstation allowances, attendance names',
    feeds: [['py-site-sheets', 'Site Sheets'], ['py-att-import', 'Attendance Import'], ['py-cost-site', 'Cost by Site'], ['py-financial', 'Financial'],
      ['py-employees', 'Employees'], ['py-entitlements', 'Entitlements'], ['attendance', 'Attendance']] },
  { key: 'py-grades', title: 'Salary Grades', Ico: Layers, holds: 'Grade amounts, daily rate, absence profile, entitlement flags',
    feeds: [['py-employees', 'Employees'], ['py-engine', 'Payroll Engine'], ['py-grade-cost', 'Grade Cost Summary'], ['py-entitlements', 'Entitlements'],
      ['pay-grades', 'Compensation Grades']] },
  { key: 'py-grading', title: 'Detailed Grading', Ico: ListTree, holds: 'The full ladder, every step coded',
    feeds: [['py-grades', 'Salary Grades']] },
  { key: 'py-benefits-setup', title: 'Benefits Setup', Ico: Gift, holds: 'Allowances: amount, basis, frequency, who is entitled',
    feeds: [['py-entitlements', 'Entitlements'], ['py-benefits-due', 'Benefits Due'], ['py-benefits-statement', 'Benefits Statement'],
      ['py-emp-statement', 'Employee Statement'], ['py-financial', 'Financial'], ['payment-structure', 'Payment Structure'], ['benefit-policy', 'Benefit Policy']] },
  { key: 'py-holidays', title: 'Public Holidays', Ico: CalendarHeart, holds: 'Holiday dates with notes',
    feeds: [['py-date-control', 'Date Control'], ['attendance-mark', 'Mark Daily Attendance'], ['py-site-sheets', 'Site Sheets'], ['leave', 'Leave']] },
]
const AREA_TITLE = Object.fromEntries([...AREAS.map(a => [a.key, a.title]), ['py-employees', 'Employees'], ['links', 'Linked screens']])
const LEVEL = { ok: ['ok', CheckCircle2], warn: ['warn', AlertTriangle], bad: ['bad', XCircle] }

function areaStat(key, d) {
  const c = d.counts
  return {
    'py-date-control': `${d.period.label} · ${d.period.sched_wd} working days`,
    'py-setup': `${c.components} components · ${c.bands} PAYE bands · ${c.profiles} profiles · ${c.payers} payers`,
    'py-projects': `${c.sites_active} active of ${c.sites}`,
    'py-grades': `${n0(c.grades)} grades · ${c.grades_staff} with staff`,
    'py-grading': `${n0(c.steps)} steps · ${c.steps_priced} priced`,
    'py-benefits-setup': `${c.benefits_active} active · ${c.benefits_due} due this month`,
    'py-holidays': `${c.holidays_year} in ${d.period.label.slice(-4)} · ${c.holidays} in all`,
  }[key]
}

export default function SetupOverview() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const busy = false
  const [open, setOpen] = useState({})
  const [lists, setLists] = useState(false)
  const load = () => api.setupOverview().then(setD).catch(e => setErr(e.message))
  useEffect(() => { load() }, [])
  if (err) return <ErrorBox error={err} />
  if (!d) return <Loading what="the setup overview" />
  const issues = d.checks.filter(c => c.level !== 'ok')
  const out = d.links.filter(l => !l.in_step)
  const sync = () => {}
  const R = d.rules
  return (
    <div className="ps py">
      <PyHeader title="Setup Overview" sub="Everything the payroll reads before it calculates. Change it rarely — every sheet, report and linked ERP screen follows it.">
        <button className="mdr-btn" onClick={load}><RefreshCw size={14} /> Refresh</button>
        {d.can_sync && <button className="mds-run" disabled={busy} onClick={sync}><Link2 size={14} /> {out.length ? `Bring ${out.length} linked screen(s) in step` : 'Re-check linked screens'}</button>}
      </PyHeader>

      <div className="ps-kpis py-kpis py-kpis6">
        <Kpi label="Payroll Month" value={d.period.label} Ico={CalendarCog} note={`${d.period.text} · cut-off day ${d.period.start_day}`} />
        <Kpi label="Salary Grades" value={n0(d.counts.grades)} Ico={Layers} tone="blue" i={1} note={`${d.counts.grades_staff} in use · ${n0(d.counts.steps)} ladder steps`} />
        <Kpi label="Projects" value={n0(d.counts.sites_active)} Ico={MapPin} tone="teal" i={2} note={`active · ${d.counts.sites} in all`} />
        <Kpi label="Benefits" value={n0(d.counts.benefits_active)} Ico={Gift} tone="purple" i={3} note={`${d.counts.benefits_due} due in ${d.period.label}`} />
        <Kpi label="Public Holidays" value={n0(d.counts.holidays_year)} Ico={CalendarHeart} tone="amber" i={4}
          note={d.next_holiday ? `next: ${d.next_holiday.name}, ${dmy(d.next_holiday.date)}` : 'none ahead'} />
        <Kpi label="Setup Health" value={issues.length ? `${issues.length} to look at` : 'All OK'} Ico={ShieldCheck} tone={issues.length ? 'red' : 'green'} i={5}
          note={out.length ? `${out.length} linked screen(s) out of step` : 'linked ERP screens in step'} />
      </div>

      <div className="hre-grid2">
        <Panel title="Setup Health" note={`${d.checks.length - issues.length} OK · ${issues.length} to look at`}>
          <ul className="py-checks py-health">
            {[...issues, ...d.checks.filter(c => c.level === 'ok')].map((c, i) => {
              const [cls, Ico] = LEVEL[c.level]
              return <li key={i} className={cls}><Ico size={15} /><span>{c.text}</span>
                {c.fix === 'sync' ? (d.can_sync && <button className="py-link" disabled={busy} onClick={sync}>Bring in step</button>)
                  : c.area !== 'links' && <L k={c.area} className="py-link">{AREA_TITLE[c.area] || 'Open'} <ArrowRight size={12} /></L>}</li>
            })}
          </ul>
        </Panel>
        <Panel title="Key Rules in Force" note={<L k="py-setup">Setup Rules <ArrowRight size={12} /></L>}>
          <div className="py-rule-grid">
            {[['Company on payslips', R.company], ["Day's pay", `monthly ÷ ${R.divisor} days`], ['Full day', `${R.hours} hours`],
              ['Daily rate', `${pctx(R.daily_pct)} of grade amount`], ['Pension (EE · ER)', `${pctx(R.pension_ee)} · ${pctx(R.pension_er)} on ${R.pension_base}`],
              ['NHF', R.nhf_on === 'YES' ? 'deducted' : 'not deducted'], ['Missed days cut', `${R.default_profile} (default profile)`], ['Staff cost to sites by', R.alloc_mode],
            ].map(([l, v]) => <div key={l} className="py-stat"><small>{l}</small><b title={v}>{v}</b></div>)}
          </div>
          {d.period.in_period.length > 0 && <p className="muted small">Holidays in this period: {d.period.in_period.map(h => `${h.name} (${dmy(h.date)})`).join(' · ')}</p>}
          {d.last_change && <p className="muted small">Last set-up change: {d.last_change.what} — {d.last_change.by}, {dmy(d.last_change.at)}</p>}
        </Panel>
      </div>

      <div className="mdr-card py-nav-sec">
        <h3>What each Setup area feeds</h3>
        <div className="py-feed-grid">
          {AREAS.map(a => (
            <div key={a.key} className="py-feed">
              <L k={a.key} className="py-feed-head"><a.Ico size={16} /><b>{a.title}</b><ArrowRight size={13} /></L>
              <small>{a.holds}</small>
              <div className="py-feed-stat">{areaStat(a.key, d)}</div>
              <div className="py-feed-to"><span>Feeds</span>{a.feeds.map(([k, l]) => <L key={k} k={k} className="py-feed-chip">{l}</L>)}</div>
            </div>
          ))}
        </div>
      </div>

      <Panel title="Linked ERP Set-up Screens" note="Setup is the single source — these follow it">
        <div className="ps-wb"><table className="ps-table py-simple py-links">
          <thead><tr><th>ERP screen</th><th>What it takes from Setup</th><th>How</th><th>Status</th><th /></tr></thead>
          <tbody>{d.links.map(l => <Fragment key={l.key}>
            <tr>
              <td><b>{l.title}</b></td><td className="muted">{l.what}</td>
              <td>{l.mode === 'synced' ? <Chip tone="blue">Copied on every save</Chip> : <Chip tone="gray">Same records</Chip>}</td>
              <td>{l.in_step ? <Chip tone="green">In step</Chip>
                : <button className="py-link" onClick={() => setOpen({ ...open, [l.key]: !open[l.key] })}>
                  {open[l.key] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}<Chip tone="amber">{l.changes.length} difference(s)</Chip></button>}</td>
              <td className="r"><L k={l.screen} className="py-link">Open <ArrowRight size={12} /></L></td>
            </tr>
            {open[l.key] && !l.in_step && <tr className="py-link-diff"><td colSpan={5}><ul>{l.changes.map(c => <li key={c}>{c}</li>)}</ul></td></tr>}
          </Fragment>)}</tbody>
        </table></div>
        <p className="muted small">These screens read the same records as Setup, so they are always in step — there is nothing to copy.</p>
      </Panel>

      <Panel title="Drop-down Lists" note="the workbook's LISTS sheet">
        <button className="py-link" onClick={() => setLists(!lists)}>{lists ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {lists ? 'Hide' : 'Show'} the {Object.keys(d.lists).length} lists every Setup sheet picks from</button>
        {lists && <div className="py-lists">
          {Object.entries(d.lists).map(([k, v]) => <div key={k} className="py-list"><small>{k}</small><div>{v.map(x => <span key={x} className="py-feed-chip static">{x}</span>)}</div></div>)}
        </div>}
      </Panel>
    </div>
  )
}
